/**
 * RoomCodeDisplay Component
 * 
 * Reusable component for displaying room codes with sharing functionality
 * Used by both Tic-Tac-Toe and Chess multiplayer games
 */

import React, { useState } from 'react';
import { ShareButton } from './ShareButton';
import { ShareModal } from './ShareModal';
import { NotificationService } from '../services/NotificationService';
import './RoomCodeDisplay.css';

interface RoomCodeDisplayProps {
  roomCode: string;
  gameType: 'tic-tac-toe' | 'chess' | 'checkers' | 'connect-four' | 'battleship' | 'poker' | 'mafia' | 'rummy' | 'court-splitter';
  playerRole?: string; // e.g., "Player X", "White", "Black", "Dark", "Light"
  playerName?: string; // Name of the player who created the room
  maxPlayers?: number; // Maximum number of players (default: 2)
  onCancel?: () => void;
  showCancelButton?: boolean;
}

export const RoomCodeDisplay: React.FC<RoomCodeDisplayProps> = ({
  roomCode,
  gameType,
  playerRole,
  playerName = 'Guest',
  maxPlayers = 2,
  onCancel,
  showCancelButton = true
}) => {
  const [showShareModal, setShowShareModal] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(roomCode);
    NotificationService.getInstance().sendSuccessNotification('Room code copied to clipboard!');
  };

  const handleShareRoom = () => {
    setShowShareModal(true);
  };

  const gameDisplayName = gameType === 'tic-tac-toe' ? 'Tic-Tac-Toe' : gameType === 'checkers' ? 'Checkers' : gameType === 'connect-four' ? 'Connect Four' : gameType === 'battleship' ? 'Battleship' : gameType === 'poker' ? 'Poker' : gameType === 'mafia' ? 'Mafia' : gameType === 'rummy' ? 'Rummy' : gameType === 'court-splitter' ? 'Court Splitter' : 'Chess';

  return (
    <div className="room-code-display-container">
      <div className="room-code-card">
        <h2 className="room-code-title">Waiting for Opponent...</h2>
        
        <div className="room-code-section">
          <label className="room-code-label">ROOM CODE:</label>
          <div className="room-code-value">{roomCode}</div>
        </div>

        {playerRole && (
          <div className="player-role">
            <span className="role-label">You are:</span>
            <span className="role-value">{playerRole}</span>
          </div>
        )}

        <div className="room-code-actions">
          <button 
            className="copy-code-button"
            onClick={handleCopyCode}
            aria-label="Copy room code"
          >
            <span className="button-icon">📋</span>
            <span className="button-text">Copy Code</span>
          </button>

          <button 
            className="share-room-button"
            onClick={handleShareRoom}
            aria-label="Share room"
          >
            <span className="button-icon">📤</span>
            <span className="button-text">Share Room</span>
          </button>
        </div>

        <p className="room-code-instructions">
          Share this code with your opponent to start the game. They can enter it on the main menu to join.
        </p>

        <div className="waiting-indicator">
          <div className="spinner"></div>
          <p className="waiting-text">Waiting for player to join...</p>
        </div>

        {showCancelButton && onCancel && (
          <button 
            className="cancel-button"
            onClick={onCancel}
            aria-label="Cancel and return to menu"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareType="room"
          shareData={{
            roomCode,
            gameType,
            playerName,
            maxPlayers,
            message: `Join my ${gameDisplayName} game! 🎮`,
            url: `${window.location.origin}/${gameType}?roomCode=${roomCode}`,
            title: `${gameDisplayName} Game Invitation`
          }}
        />
      )}
    </div>
  );
};

export default RoomCodeDisplay;
